import type { CalendarEvent } from "../models/calendar.model"

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function hasEvent(date: Date, events: CalendarEvent[]) {
  return events.some(e => sameDay(e.date, date))
}

function getMonthGrid(year: number, month: number): (Date | null)[] {
  const firstDay = new Date(year, month, 1).getDay()
  const total = new Date(year, month + 1, 0).getDate()
  const cells: (Date | null)[] = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= total; d++) cells.push(new Date(year, month, d))
  return cells
}

interface MiniMonthProps {
  year: number
  month: number
  today: Date
  events: CalendarEvent[]
  onSelectDate: (d: Date) => void
}

function MiniMonth({ year, month, today, events, onSelectDate }: MiniMonthProps) {
  const cells = getMonthGrid(year, month)
  return (
    <div className="flex flex-col">
      <p className="text-sm font-semibold text-foreground mb-2">{MONTHS[month]}</p>
      <div className="grid grid-cols-7 gap-px">
        {DAY_LABELS.map((d, i) => (
          <div key={i} className="text-center text-[9px] font-medium text-muted-foreground py-0.5">
            {d}
          </div>
        ))}
        {cells.map((date, i) => {
          if (!date) return <div key={`e-${i}`} />
          const isToday = sameDay(date, today)
          const hasEv = hasEvent(date, events)
          return (
            <button
              key={i}
              onClick={() => onSelectDate(date)}
              className={`
                relative w-full aspect-square flex items-center justify-center text-[10px] rounded-full transition-colors
                ${isToday
                  ? "bg-primary text-white font-bold"
                  : "text-foreground/70 hover:bg-primary/10 hover:text-primary"
                }
              `}
            >
              {date.getDate()}
              {hasEv && !isToday && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-0.5 h-0.5 rounded-full bg-primary" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface Props {
  currentDate: Date
  events: CalendarEvent[]
  onSelectDate: (d: Date) => void
}

export default function YearView({ currentDate, events, onSelectDate }: Props) {
  const today = new Date()
  const year = currentDate.getFullYear()

  return (
    <div className="h-full overflow-y-auto px-6 py-4">
      <div className="grid grid-cols-4 gap-8 max-w-5xl mx-auto">
        {Array.from({ length: 12 }, (_, i) => (
          <MiniMonth
            key={i}
            year={year}
            month={i}
            today={today}
            events={events}
            onSelectDate={onSelectDate}
          />
        ))}
      </div>
    </div>
  )
}
